import type { Payout } from '../types/payout';

/**
 * Représente une ligne du formulaire 2086 (Article 150 VH bis du CGI).
 * Chaque entrée correspond à un événement de cession (vente ou côté envoyé d'un swap).
 */
export interface Form2086Entry {
  disposalId: string;
  crypto: { id: string; symbol: string; name: string };
  amountSold: number;
  // Cases du formulaire 2086
  case211: string;  // Date de la cession
  case212: number;  // Valeur globale du portefeuille en € à la date de cession (saisie utilisateur)
  case213: number;  // Prix de cession brut
  case214: number;  // Frais imputables à la cession
  case215: number;  // Soultes versées lors de la cession (généralement 0)
  case216: number;  // Soultes reçues lors de la cession (généralement 0)
  case217: number;  // Prix de cession net = 213 - 214 + 215 - 216
  case218: number;  // Frais d'acquisition du bien cédé (généralement 0, inclus dans 220)
  case220: number;  // Prix total d'acquisition du portefeuille (cumulatif jusqu'à la cession)
  case221: number;  // Fractions de capital initial récupérées lors des cessions antérieures
  case222: number;  // Soultes reçues lors d'échanges antérieurs (généralement 0)
  case223: number;  // Prix total d'acquisition net = 220 - 221 - 222
  case224: number;  // Plus-value nette = 217 - (223 × 217 / 212)
  missingPortfolioValue: boolean; // true si la valeur portefeuille (212) est absente → calcul impossible
}

/**
 * Calcule les plus-values sur actifs numériques selon la méthode légale française
 * définie à l'Article 150 VH bis du CGI (formulaire 2086).
 *
 * Formule :
 *   Plus-value (224) = 217 - (223 × 217 / 212)
 *
 * Avec :
 *   217 = Prix de cession net (brut − frais)
 *   212 = Valeur globale du portefeuille en € à la date de cession (fournie par l'utilisateur)
 *   223 = 220 − 221 − 222
 *   220 = Cumul de tous les coûts d'acquisition (portefeuille entier, depuis le début)
 *   221 = Fractions de capital récupérées lors des cessions précédentes (cumulatif, mis à jour après chaque cession)
 *   222 = Soultes reçues lors d'échanges (généralement 0)
 *
 * Mise à jour de 221 après chaque cession :
 *   nouvelle_221 = ancienne_221 + (223 × 217 / 212)
 *
 * @param transactions - Toutes les transactions (triées par date en interne)
 * @param initialAcquisitionTotal - Valeur initiale de la case 220 (acquisitions avant utilisation de l'app)
 * @param initialCapitalRecovered - Valeur initiale de la case 221 (capital déjà récupéré avant utilisation de l'app)
 */
export const calculateForm2086 = (
  transactions: Payout[],
  initialAcquisitionTotal = 0,
  initialCapitalRecovered = 0
): Form2086Entry[] => {
  const entries: Form2086Entry[] = [];

  const sorted = [...transactions].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  let running220 = initialAcquisitionTotal;
  let running221 = initialCapitalRecovered;

  for (const tx of sorted) {
    switch (tx.type) {
      case 'payout':
      case 'achat':
      case 'stacking':
        // Les acquisitions (minage, achats, stacking) alimentent le total 220
        running220 += tx.totalValue ?? 0;
        break;

      case 'vente': {
        if (!tx.crypto || tx.amount === undefined || tx.unitPrice === undefined) break;

        const case213 = tx.totalValue ?? tx.amount * tx.unitPrice;
        const case214 = tx.fees ?? 0;
        const case217 = case213 - case214;
        const case220snap = running220;
        const case221snap = running221;
        const case223 = Math.max(0, case220snap - case221snap); // 222 = 0
        const missingPortfolioValue = !tx.portfolioValueAtSale;
        const case212 = tx.portfolioValueAtSale ?? 0;

        let case224 = 0;
        if (!missingPortfolioValue && case212 > 0) {
          const capitalFraction = (case223 * case217) / case212;
          case224 = case217 - capitalFraction;
          running221 += capitalFraction;
        }

        entries.push({
          disposalId: tx.id,
          crypto: tx.crypto,
          amountSold: tx.amount,
          case211: tx.date,
          case212,
          case213,
          case214,
          case215: 0,
          case216: 0,
          case217,
          case218: 0,
          case220: case220snap,
          case221: case221snap,
          case222: 0,
          case223,
          case224,
          missingPortfolioValue,
        });
        break;
      }

      case 'swap':
        // Crypto-to-crypto swaps are not taxable events under French law (CGI 150 VH bis).
        // The acquisition cost of the sent asset remains in running220 and transfers implicitly
        // to the received asset. No cession entry is created; running220 is not updated.
        break;
    }
  }

  return entries;
};
